<script setup>
import { computed, ref, onMounted, provide, watch, inject, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useReCaptcha } from 'vue-recaptcha-v3';
import _ from 'lodash';

import FormHeader from '@/components/form/FormHeader.vue';
import FormContent from '@/components/form/FormContent.vue';
import FormFooter from '@/components/form/FormFooter.vue';
import ApiService from '@/services/ApiService';

const form = ref(null);
const submission = ref(null);
const alerts = ref([]); // Use ref for arrays so template updates when you .push()
const files = ref([]);
const route = useRoute();
const isSendDisabled = ref(false);
const isSubmitting = ref(false);
const formElementRef = ref(null);
const formRenderKey = ref(0);
const confirmDialog = inject('vue3-confirm-dialog-box', null);

const collectEmailValues = (elements = [], values = []) => {
  elements.forEach((element) => {
    if (element.type === 'email' && typeof element.value === 'string') {
      const trimmedValue = element.value.trim();
      if (trimmedValue) {
        values.push(trimmedValue);
      }
    }

    if (Array.isArray(element.elements)) {
      collectEmailValues(element.elements, values);
    }

    if (Array.isArray(element.rows)) {
      element.rows.forEach((row) => {
        if (Array.isArray(row)) {
          collectEmailValues(row, values);
        }
      });
    }
  });

  return values;
};

const applyPrimaryEmailToFields = (elements = [], primaryEmail, state = { sourceConsumed: false }) => {
  let changed = false;

  elements.forEach((element) => {
    if (element.type === 'email') {
      const currentValue = typeof element.value === 'string' ? element.value.trim() : '';
      const isSourceField = !state.sourceConsumed && currentValue === primaryEmail;

      if (isSourceField) {
        state.sourceConsumed = true;
        element._syncWithPrimaryEmail = false;
        element._lastAutoFilledPrimaryEmail = '';
      } else if (!currentValue) {
        if (element.value !== primaryEmail) {
          element.value = primaryEmail;
          changed = true;
        }
        element._syncWithPrimaryEmail = true;
        element._lastAutoFilledPrimaryEmail = primaryEmail;
      } else if (element._syncWithPrimaryEmail === true) {
        if (currentValue !== (element._lastAutoFilledPrimaryEmail || '')) {
          element._syncWithPrimaryEmail = false;
          element._lastAutoFilledPrimaryEmail = '';
        } else if (element.value !== primaryEmail) {
          element.value = primaryEmail;
          element._lastAutoFilledPrimaryEmail = primaryEmail;
          changed = true;
        }
      }
    }

    if (Array.isArray(element.elements)) {
      changed = applyPrimaryEmailToFields(element.elements, primaryEmail, state) || changed;
    }

    if (Array.isArray(element.rows)) {
      element.rows.forEach((row) => {
        if (Array.isArray(row)) {
          changed = applyPrimaryEmailToFields(row, primaryEmail, state) || changed;
        }
      });
    }
  });

  return changed;
};

const sharedEmailSuggestion = computed(() => {
  if (!form.value?.elements) {
    return '';
  }

  return collectEmailValues(form.value.elements)[0] || '';
});

provide('sharedEmailSuggestion', sharedEmailSuggestion);

watch(sharedEmailSuggestion, async (primaryEmail) => {
  if (!primaryEmail || !form.value?.elements) {
    return;
  }

  const didChange = applyPrimaryEmailToFields(form.value.elements, primaryEmail);

  if (didChange) {
    await updateSubmissionData();
  }
});

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();
const getRecaptchaToken = async (action) => {
  await recaptchaLoaded();
  const token = await executeRecaptcha(action);
  // console.log('✅ reCAPTCHA token generated:', token);
  return token;
};

const showAlert = (variant = 'warning', message, duration) => {
  const id = Date.now() + Math.random().toString(36).substring(2, 9);
  // console.log('creating a new alert with id: %o', id);
  alerts.value.push({ id, variant, message, duration });
};

const handleDismissAlert = (idToDismiss) => {
  // console.log('handleDismissAlert received id:', idToDismiss, ' (Type:', typeof idToDismiss, ')');
  // console.log('Alerts before removal (full objects):', alerts.value);
  // console.log('IDs in array before filter:', alerts.value.map(a => a.id));

  const newAlerts = alerts.value.filter(a => {
    // console.log(`  Filtering: Comparing alert ID "${a.id}" (Type: ${typeof a.id}) with dismiss ID "${idToDismiss}" (Type: ${typeof idToDismiss})`);
    // console.log(`  Comparison result (a.id !== idToDismiss): ${a.id !== idToDismiss}`);
    return a.id !== idToDismiss;
  });

  alerts.value = newAlerts;
  // console.log('Alerts after removal (full objects):', alerts.value);
  // console.log('IDs in array after filter:', alerts.value.map(a => a.id));
};

const clearElementValues = (elements = [], options = {}) => {
  const { respectKeepValue = false } = options;

  elements.forEach((element) => {
    // Skip clearing if element has keepValue enabled (only when respectKeepValue is true)
    if (respectKeepValue && element.keepValue) {
      return;
    }

    if (element.type === 'checkbox') {
      element.checked = false;
    } else if (element.type === 'radio') {
      const initialChecked = Boolean(element._initialChecked);
      element.checked = initialChecked;
      element.selected = initialChecked ? element.value : false;
    } else if (element.type === 'select') {
      element.value = Object.prototype.hasOwnProperty.call(element, '_initialValue') ? element._initialValue : '';
    } else if (element.type === 'file') {
      element.files = [];
    } else if (Object.prototype.hasOwnProperty.call(element, 'value')) {
      element.value = '';
    }

    if (Object.prototype.hasOwnProperty.call(element, '_initialCollapsed')) {
      element.collapsed = element._initialCollapsed;
    }

    if (Object.prototype.hasOwnProperty.call(element, 'disabled')) {
      element.disabled = false;
    }

    if (Array.isArray(element.elements)) {
      clearElementValues(element.elements, options);
    }

    if (Array.isArray(element.rows)) {
      element.rows.forEach((row) => {
        if (Array.isArray(row)) {
          clearElementValues(row, options);
        }
      });
    }
  });
};

const reapplyDynamicState = (elements = []) => {
  elements.forEach((element) => {
    if (element.type === 'radio' && element.checked) {
      if (element.section !== undefined) {
        checkRef(form.value.elements, element.section, element.name)
        syncRadioGroupSelection(form.value.elements, element.section, element.name, element.value)
      }

      if (element.set) {
        setValue(form.value.elements, element.set.name, element.set.value)
      }
    }

    if (element.type === 'select' && Array.isArray(element.options)) {
      const selectedOption = element.options.find((option) => option.value === element.value)

      if (selectedOption?.children) {
        let children = [...selectedOption.children]
        let index

        if (element.name && !isNaN(element.name.substr(-1))) {
          index = element.name.match(/-(\d+)$/)?.[1]
        }

        if (index !== undefined) {
          children = children.map((child) => `${child}-${index}`)
        }

        expandChildren(form.value.elements, children, element.name, index)
      } else if (selectedOption?.section !== undefined) {
        let name = element.name
        const isIndexed = !isNaN(element.name?.substr(-1))
        if (isIndexed) {
          name = name.replace(/-\d+$/, '')
        }
        checkRef(form.value.elements, selectedOption.section, name)
      }
    }

    if (Array.isArray(element.elements)) {
      reapplyDynamicState(element.elements)
    }

    if (Array.isArray(element.rows)) {
      element.rows.forEach((row) => {
        if (Array.isArray(row)) {
          reapplyDynamicState(row)
        }
      })
    }
  })
}

const resetForm = () => {
  clearElementValues(form.value?.elements || [], { respectKeepValue: true });
  reapplyDynamicState(form.value?.elements || []);
};

const prepareNewOrder = async () => {
  if (!form.value?.elements) {
    return;
  }

  resetForm();
  isSendDisabled.value = false;
  isSubmitting.value = false;
  alerts.value = [];
  formElementRef.value?.classList.remove('was-validated');
  formElementRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  await updateSubmissionData();
};

const resetFormCompletely = async () => {
  clearElementValues(form.value?.elements || [], { respectKeepValue: false });
  reapplyDynamicState(form.value?.elements || []);
  files.value = [];
  alerts.value = [];
  isSendDisabled.value = false;
  isSubmitting.value = false;
  formElementRef.value?.classList.remove('was-validated');
  formRenderKey.value += 1;
  await nextTick();
  formElementRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  await updateSubmissionData();
};

const showSubmissionSuccessDialog = (message) => {
  if (!confirmDialog) {
    showAlert('success', message, 5000);
    return;
  }

  confirmDialog({
    title: '<span style="color: #198754;">Bekræftet</span>',
    message: `
      <br>
      <span style="display: inline-block; color: #198754; font-size: 2rem; line-height: 1;">&#10003;</span>
      <br>
      <strong>${message}</strong>
    `,
    button: {
      yes: 'Opret ny ordre',
    },
    callback: async (confirmed) => {
      if (confirmed) {
        await prepareNewOrder();
      }
    },
  });
};

const updateSubmissionData = async () => {
  if (!form.value) return;
  let sub = { ..._.pick(form.value, ["_id", "title"]) };
  sub.elements = await populateTablerefs(form.value.elements);
  submission.value = sub;
};

const handleFilesBeforeSave = (updatedFiles) => {
  showAlert('success', 'File(s) was uploaded to the form. Now save!', 1000);
  files.value = updatedFiles;
};

const onFormChanged = () => {
  updateSubmissionData();
};

const uploadFiles = async () => {
  let formData = new FormData();
  files.value.forEach((file) => {
    formData.append("file", file);
  });

  try {
    return await ApiService.uploadFiles(form.value._id, formData);
  } catch (error) {
    console.log(error);
    return error;
  }
};

const submitForm = async (recaptchaToken) => {
  try {
    const payload = {
      ...submission.value,
      form: form.value._id,
      'g-recaptcha-response': recaptchaToken,
    };
    return await ApiService.submitForm(payload);
  } catch (error) {
    console.log(error);
    return error;
  }
};

const printHtmlDocument = (html) => {
  return new Promise((resolve, reject) => {
    const printFrame = document.createElement('iframe');
    let cleanupTimer;

    const cleanup = () => {
      if (cleanupTimer) {
        window.clearTimeout(cleanupTimer);
      }

      if (printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
    };

    const finalizeSuccess = () => {
      cleanup();
      resolve();
    };

    const finalizeError = (error) => {
      cleanup();
      reject(error);
    };

    printFrame.setAttribute('title', 'print-frame');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.visibility = 'hidden';

    printFrame.onload = () => {
      const frameWindow = printFrame.contentWindow;

      if (!frameWindow) {
        finalizeError(new Error('Could not access print preview window.'));
        return;
      }

      const runPrint = () => {
        try {
          frameWindow.focus();

          if ('onafterprint' in frameWindow) {
            frameWindow.onafterprint = finalizeSuccess;
          }

          frameWindow.print();

          // Firefox can skip onafterprint for iframe printing, so keep a fallback cleanup.
          cleanupTimer = window.setTimeout(finalizeSuccess, 1500);
        } catch (error) {
          finalizeError(error);
        }
      };

      window.setTimeout(runPrint, 150);
    };

    document.body.appendChild(printFrame);

    const doc = printFrame.contentDocument || printFrame.contentWindow?.document;

    if (!doc) {
      finalizeError(new Error('Could not initialize print document.'));
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();
  });
};

const onSubmit = async (event) => {
  const formElement = event.target;
  // console.log('onSubmit triggered...');

  // Temporarily remove required attribute from collapsed/hidden elements
  const inputsToRestore = [];

  // Method to check if an element is hidden or inside a collapsed section
  const isElementHidden = (element) => {
    // Check if element itself is hidden or disabled
    if (element.disabled || element.hidden) {
      return true;
    }

    const elementStyle = window.getComputedStyle(element);
    if (elementStyle.display === 'none' || elementStyle.visibility === 'hidden') {
      return true;
    }

    // Check if element is inside a disabled fieldset (collapsed sections)
    // This is the primary indicator of collapsed sections
    let parent = element.parentElement;
    while (parent && parent !== formElement) {
      // Check for disabled fieldset (collapsed sections use this)
      if (parent.tagName === 'FIELDSET' && parent.disabled) {
        return true;
      }

      // Check if parent container is hidden
      const style = window.getComputedStyle(parent);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return true;
      }

      // Check for collapsed container patterns (height 0 with overflow hidden)
      const height = parseFloat(style.height);
      if (height === 0 && style.overflow === 'hidden' && style.maxHeight === '0px') {
        return true;
      }

      parent = parent.parentElement;
    }
    return false;
  };

  // Check all required inputs and remove required from hidden/collapsed ones
  const allRequiredInputs = formElement.querySelectorAll('input[required], select[required], textarea[required]');
  allRequiredInputs.forEach((input) => {
    if (isElementHidden(input)) {
      input.removeAttribute('required');
      inputsToRestore.push(input);
    }
  });

  // Also check by element names from form data structure (for elements that might not be in DOM yet)
  const collapsedNames = getCollapsedElementNames(form.value.elements);
  if (collapsedNames.length > 0) {
    collapsedNames.forEach((name) => {
      const inputs = formElement.querySelectorAll(`[name="${name}"]`);
      inputs.forEach((input) => {
        if (input.hasAttribute('required') && !inputsToRestore.includes(input)) {
          input.removeAttribute('required');
          inputsToRestore.push(input);
        }
      });
    });
  }

  if (!formElement.checkValidity()) {
    event.preventDefault();
    event.stopPropagation();
    formElement.classList.add('was-validated');
    showAlert('danger', 'OBS: Formularen er ikke sendt. De rødmarkerede felter ovenfor skal udfyldes, før formularen kan sendes.', 5000);

    // Restore required attributes before returning
    inputsToRestore.forEach((input) => {
      input.setAttribute('required', '');
    });

    return false;
  }

  // Restore required attributes after successful validation
  inputsToRestore.forEach((input) => {
    input.setAttribute('required', '');
  });

  isSubmitting.value = true;

  let recaptchaToken;

  try {
    recaptchaToken = await getRecaptchaToken('submit');
  } catch (error) {
    console.error('reCAPTCHA execution failed:', error);
    showAlert('danger', 'Could not verify you are human. Please try again.', 3000);
    isSubmitting.value = false;
    return;
  }

  try {
    if (files.value.length > 0) {
      const upload = await uploadFiles();
      showAlert(upload.data.variant || 'danger', upload.data.message, 3000);
    }
  } catch (error) {
    showAlert('danger', error.message, 3000);
    isSubmitting.value = false;
  }

  try {
    const subResponse = await submitForm(recaptchaToken);
    // console.log('submission done', subResponse);


    // console.log('send email to ID', subResponse.data.result._id);
    const email = await ApiService.sendEmail(subResponse.data.result._id);
    if (email.data.variant === 'success') {
      const successMessage = email.data.message;
      showSubmissionSuccessDialog(successMessage);
      // NOTE: check if it was something to print
      if (email.data.sendResult.html && email.data.isprint) {

        const originalHtml = email.data.sendResult.html;
        const printStyles = `
<style>
  @media print {
    @page {
      size: A4 portrait;
      margin: 0;
    }
    body {
      font-family: 'Times New Roman', serif;
      background-color: #fff;
      color: #000;
      zoom: 1;
    }
    * {
      color: #000 !important;
      background: transparent !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }
    div > table > tbody > tr > td > table > tbody {
      page-break-inside: avoid;
    }
    p[style*="border-top: 1px solid grey"] {
      border-top: 1px solid grey !important; /* Re-apply the border */
      height: 1px !important;                 /* Ensure it has height */
      display: block !important;              /* Ensure it's not hidden */
      margin: 1rem 0 !important;
    }
  }
</style>
`;

        const styledHtml = printStyles + originalHtml;
        await printHtmlDocument(styledHtml);

        isSendDisabled.value = false;
      } else {
        isSendDisabled.value = true;
      }

      isSubmitting.value = false;
    } else {
      showAlert(email.data.variant || 'warning', email.data.message, 5000);
      isSubmitting.value = false;
    }
  } catch (error) {
    showAlert('danger', error.message, 5000);
    isSendDisabled.value = false;
    isSubmitting.value = false;
  }
};

const disableElement = (elems, section, disabled) => {
  elems.forEach((el) => {
    if (el.section == section) {
      el.disabled = disabled
    }
    if (el.elements) {
      disableElement(el.elements, section, disabled)
    }
  })
}
const initializeRadioSelections = (elements = []) => {
  elements.forEach((element) => {
    if (typeof element._initialCollapsed === 'undefined' && Object.prototype.hasOwnProperty.call(element, 'collapsed')) {
      element._initialCollapsed = element.collapsed
    }

    if (element.type === 'select' && typeof element._initialValue === 'undefined') {
      element._initialValue = Object.prototype.hasOwnProperty.call(element, 'value') ? element.value : ''
    }

    if (element.type === 'radio') {
      if (typeof element._initialChecked === 'undefined') {
        element._initialChecked = Boolean(element.checked)
      }
      element.selected = element.checked ? element.value : false
    }

    if (Array.isArray(element.elements)) {
      initializeRadioSelections(element.elements)
    }

    if (Array.isArray(element.rows)) {
      element.rows.forEach((row) => {
        if (Array.isArray(row)) {
          initializeRadioSelections(row)
        }
      })
    }
  })
}

const setValue = (elems, name, value) => {
  elems.forEach((el) => {
    if (el.name == name) {
      if (el.type == 'radio') {
        const isSelected = el.value == value
        el.checked = isSelected
        el.selected = isSelected ? el.value : false
      } else {
        el.value = value
      }
    }
    if (el.elements) {
      setValue(el.elements, name, value)
    }
  })
}
const expandChildren = (elems, children, name, index) => {
  // console.log('name', name)
  if (name) {
    elems.forEach((el) => {
      if (el.rows && el.type == 'table') {
        el.rows.forEach((row, j) => {
          if (j == index) {
            row.map((col) => { // Collapse all related columns
              col.collapsed = col.ref == name
              if (col.elements) {
                col.elements = col.elements.map((subCol) => {
                  // console.log('subcol', _.pick(subCol, ["name", "ref", "collapsed"]), subCol.ref == name , name)
                  subCol.collapsed = subCol.ref == name
                  return subCol
                })
                col.elements.forEach((subCol) => {
                  if (children.includes(subCol.id)) {
                    subCol.collapsed = false
                  }
                })
              }
              return col
            })
            row.forEach((col) => {
              if (children.includes(col.id)) {
                col.collapsed = false
              }
            })
          }
        })
      }
      if (el.elements) {
        expandChildren(el.elements, children, name, index)
      }
    })
  }
}

const checkRef = (elems, section, name) => {
  elems.forEach((el) => {
    if ((el.type == 'table' || el.type == 'section' || el.type == 'multi') && (el.ref && name && el.ref == name)) {
      // console.log('id', el.id, 'section', section, 'name', name)
      el.collapsed = el.id !== section
    }
    if (el.elements) {
      checkRef(el.elements, section, name)
    }
  })
}

const syncRadioGroupSelection = (elems, section, name, selectedValue) => {
  elems.forEach((el) => {
    if (el.type === 'radio' && el.name === name) {
      const isSelected = el.value === selectedValue
      el.checked = isSelected
      el.selected = isSelected ? el.value : false
    }
    if (el.elements) {
      syncRadioGroupSelection(el.elements, section, name, selectedValue)
    }
  })
}

const onCheckboxChanged = (el) => {
  if (el.set && el.checked == true) {
    setValue(form.value.elements, el.set.name, el.set.value)
  }

  if (el.disable && el.disable.length) { // Array with objects describing which elements to disable
    el.disable.map((disable) => {
      disableElement(form.value.elements, disable.section, el.checked)
    })

  }

  if (el.section === undefined) {
    return
  }

  // Change collapse status of section with id = [el.id] in this.form.elements
  form.value.elements.map(elem => {
    if (elem.id === el.section) {
      elem.collapsed = !elem.collapsed
    }
    if (elem.elements) {
      elem.elements.map(subElem => {
        if (subElem.id === el.section) {
          subElem.collapsed = !subElem.collapsed
        }
      })
    }
    return elem
  })
};

const onRadioChanged = (e) => {
  const section = e.target.dataset.section
  const name = e.target.name
  const value = e.target.value

  if (section === undefined) {
    return
  }

  const elements = form.value.elements
  checkRef(elements, section, name)

  syncRadioGroupSelection(elements, section, name, value)

  if (e.target.dataset.set === undefined) {
    return
  }

  const getSet = (set) => {
    try {
      return JSON.parse(e.target.dataset.set)
    } catch (error) {
      console.log('Error parsing JSON', error, e.target.dataset.set)
    }
    return set
  }

  const set = getSet(e.target.dataset.set)

  setValue(elements, set.name, set.value)
};

const onSelectChanged = (e) => {
  const elements = form.value.elements
  const name = e.target.name
  const selectedOption = e.target.options[e.target.selectedIndex]

  // Custom values may not map to a real <option>, so skip section/children handling.
  if (!selectedOption) {
    return
  }

  if (selectedOption.dataset.children) { // Option have children
    let children = JSON.parse(selectedOption.dataset.children)
    // let children = e.target.options[e.target.selectedIndex].dataset.children.split(',') || []
    if (selectedOption.dataset.index) {
      var index = selectedOption.dataset.index

      children.map((child, i) => {
        children[i] = child + '-' + selectedOption.dataset.index
      })
    }

    expandChildren(elements, children, name, index)
  } else {
    const section = selectedOption.dataset.section
    let name = e.target.name
    // console.log('section', section, 'name', name)
    const isIndexed = !isNaN(e.target.name.substr(-1))
    if (isIndexed) {
      name = name.replace(/-\d+$/, '')
    }
    if (section === undefined) {
      return
    }
    checkRef(elements, section, name)
  }

};

const getCollapsedElementNames = (elements, collapsedNames = [], parentCollapsed = false) => {
  elements.forEach((el) => {
    // Check if this element is collapsed or inside a collapsed parent
    const isCollapsed = el.collapsed === true || parentCollapsed;

    // If this element is collapsed or inside a collapsed parent, add its name
    if (isCollapsed && el.name) {
      collapsedNames.push(el.name);
    }

    // Check if this is a collapsed container (section/multi/table)
    const isCollapsedContainer = (el.type === 'section' || el.type === 'multi' || el.type === 'table') && el.collapsed === true;

    // Recursively check children, passing down the collapsed state
    if (el.elements) {
      getCollapsedElementNames(el.elements, collapsedNames, isCollapsedContainer || isCollapsed);
    }
  });
  return collapsedNames;
};

const populateTablerefs = (elements) => {
  // It's better to use Promise.all to make it truly async.
  const promises = elements.map(async (el) => {
    if (el.type == 'tableref') {
      const response = await ApiService.getTable(el.tid);
      _.merge(el, response.data);
    }
    if (el.elements) {
      el.elements = await populateTablerefs(el.elements); // Recurse
    }
    return el;
  });
  return Promise.all(promises);
};

const loadForm = async () => {
  try {
    const response = await ApiService.getForm(route.params.formid);
    const formData = response.data;
    formData.elements = await populateTablerefs(formData.elements);
    initializeRadioSelections(formData.elements);
    form.value = formData;
    reapplyDynamicState(formData.elements);
    await updateSubmissionData();
  } catch (error) {
    console.log(error);
  }
};

onMounted(async () => {
  await loadForm();
});
</script>

<template>
  <div v-if="form" class="container p-0">
    <form :name="form.name" :id="'form-' + form._id" ref="formElementRef" method="post" action=""
      @submit.prevent="onSubmit" @change="onFormChanged" class="needs-validation" novalidate>
      <FormHeader :form="form"></FormHeader>
      <FormContent :key="formRenderKey" :is-send-disabled="isSendDisabled" :is-submitting="isSubmitting" :form="form"
        @checkbox-changed="onCheckboxChanged" @radio-changed="onRadioChanged" @select-changed="onSelectChanged"
        @on-files-updated="handleFilesBeforeSave" @reset-form="resetFormCompletely" :alerts="alerts"
        @dismiss-alert="handleDismissAlert"></FormContent>
      <FormFooter></FormFooter>
    </form>
  </div>
</template>

<style lang="scss">
.vc-btn-grid.isMono {
  .vc-btn.left {
    display: none;
  }

  .vc-btn:not(.left) {
    border-radius: 0 0 1rem 1rem;
  }
}
</style>
